//#region Request types
export type BaseStatsModel = {
	RequestType: RequestType;
	BettingTypes: string[];
	Sport: string;
	Tournament: string;
};
export type TeamStatsRequest = BaseStatsModel & {
	HomeTeam: string;
	AwayTeam: string;
	HomeTeamGender: string | null;
	AwayTeamGender: string | null;
	HomeTeamCountryCode: string | null;
	AwayTeamCountryCode: string | null;
	Category: string;
	//Only for overUnder "0.5,1.0,1.5"
	OverUnderThresholds?: string;
	//Only for 3way "0.5,1.0,1.5"
	HomeWinOdds?: number;
	DrawOdds?: number;
	HomeLossOdds?: number;
};

export type PlayerStatsRequest = BaseStatsModel & {
	Players: string;
};

export type StatsRequestModel = {
	specific:
		| TeamStatsRequest
		| PlayerStatsRequest
		| MatchStatRequest
		| EventInsightsRequest;
};

export interface MatchStatRequest {
	RequestType: string;
	Events: IEventStatsBase[];
}

export interface IEventStatsBase {
	EventId: string;
}

//#endregion

//#region Response types

export type AvailabilityData = {
	[key: string]: string;
};

export type GoalDifference = {
	frequency: number;
	scoreDifference: number;
};

export enum RecentForm {
	D = "D",
	L = "L",
	W = "W",
	NONE = "",
}

export interface BaseBettingType {
	teamAbrv: string;
	playerAbrv?: string;
	tournamentAbrv: string;
	timeStamp: string;
}

export interface DoubleChance extends BaseBettingType {
	bettingTypeAbrv: "double-chance";
	data?: {
		eventCount?: number;
		gamesWonOrDrawn?: number;
	};
}

export interface IPlayerStatistics extends BaseBettingType {
	bettingTypeAbrv: string;
	data?: {
		subbedOn?: number;
		matchesStarted?: number;
		appearances?: number;
		goals?: number;
		scoringFrequency?: number;
		expectedGoals?: number;
	};
}

export interface HandicapHCP extends BaseBettingType {
	bettingTypeAbrv: "handicap-hcp";
	data?: {
		eventCount?: number;
		commonGoalDifference?: GoalDifference[];
		goalDifference?: GoalDifference[];
	};
}

export interface WhoWinsFirstHalf extends BaseBettingType {
	bettingTypeAbrv: "who-wins-1st-half";
	data?: {
		eventCount?: number;
		gamesWonFirstHalf?: number;
	};
}

export interface WillBothTeamsScore extends BaseBettingType {
	bettingTypeAbrv: "will-both-teams-score";
	data?: {
		eventCount?: number;
		cleanSheets?: number;
		atLeastOneGoalScored?: number;
	};
}

export interface OverUnder extends BaseBettingType {
	bettingTypeAbrv: "over-under";
	data?: {
		eventCount?: number;
		avgGoalsScored?: number;
		avgGoalsConceded?: number;
		avgMatchGoals?: number;
		overUnderData?: { frequency?: number; threshold?: string }[];
	};
}

export interface WhoWinsSecondHalf extends BaseBettingType {
	bettingTypeAbrv: "who-wins-2nd-half";
	data?: {
		eventCount?: number;
		gamesWonSecondHalf?: number;
	};
}

export interface FirstHalfTotals extends BaseBettingType {
	bettingTypeAbrv: "1st-half-totals";
	data?: HalfTotals;
}

export interface HalfTotals {
	eventCount?: number;
	averageGoalsScoredFirstHalf?: number;
	averageGoalsScoredSecondHalf?: number;
	avgGoalsScored?: number;
	combinedAverageGoals?: number;
}
export interface SecondHalfTotals extends BaseBettingType {
	bettingTypeAbrv: "2nd-half-over-under";
	data: HalfTotals;
}
export interface ThreeWay extends BaseBettingType {
	bettingTypeAbrv: "3way";
	data?: {
		recentForm?: RecentForm[];
		recentFormPercentage?: number;
		homeLossProbability?: number;
		homeWinProbability?: number;
		drawProbability?: number;
	};
}

export interface CorrectScore extends BaseBettingType {
	bettingTypeAbrv: "correct-score-score-max-6";
	data?: {
		eventCount?: number;
		frequentScorelines?: {
			awayScore?: number;
			frequency?: number;
			homeScore?: number;
		}[];
	};
}
export interface FirstTeamToScore extends BaseBettingType {
	bettingTypeAbrv: "goalnr-goal";
	data?: { scoredFirstGoal?: number; eventCount?: number };
}

export interface PlayerStatsData extends BaseBettingType {
	subbedOn: number;
	matchesStarted: number;
	appearances: number;
	goals: number;
	scoringFrequency: number;
	expectedGoals: number;
}

export type BettingData =
	| DoubleChance
	| HandicapHCP
	| WhoWinsFirstHalf
	| WillBothTeamsScore
	| OverUnder
	| WhoWinsSecondHalf
	| FirstHalfTotals
	| SecondHalfTotals
	| ThreeWay
	| CorrectScore
	| FirstTeamToScore
	| IPlayerStatistics;

export interface ApiResponse<T> {
	response: T[];
	statusCode: StatusCodes;
}

export interface IMatchStatsAvailability {
	data: {
		statistics: MatchStatAvailabilityIndex;
	};
	timeStamp: Date;
}

export interface ILivePlayerStatsAvailability {
	data: {
		eventPlayers: MatchStatAvailabilityIndex;
	};
	timeStamp: Date;
}

export interface IMatchStatsResponse {
	data: IMatchStatsData;
	timeStamp: string;
	eventId?: string;
}

export interface ILivePlayerStatsResponse {
	data: ILivePlayerStatsData;
	timeStamp: string;
	eventId?: string;
}

export interface IMatchStatsData {
	pressureIndex: IPressureIndex[];
	periodTime: number;
	periodCount: number;
	statistics: IStatistics;
}

export interface ILivePlayerStatsData {
	periodTime: number;
	periodCount: number;
	eventPlayers: IEventPlayers;
}

export interface IEventPlayers {
	homeTeamPlayers: IEventPlayer[];
	awayTeamPlayers: IEventPlayer[];
}

export interface IEventPlayer {
	name: string;
	position: string;
	rating: number;
	targetId?: string;
	country: string;
	jerseyNumber: string;
	yellowCard?: number;
	redCard?: number;
	substitute?: boolean;
	ownGoals: number;
	goals: number;
	statistics: ILivePlayerStatistics;
}

interface ILivePlayerStatistics {
	[key: string]: {
		[key: string]: number | string | boolean | null;
	};
}

export interface IPressureIndex {
	minute: number;
	value: number;
}

export interface IStatistics {
	statisticItems: IPeriodStatistic[];
}

export interface IPeriodStatistic {
	key: string;
	statisticsType: StatisticsType;
	teamOneDisplayValue: string;
	teamOneValue: number;
	teamTwoDisplayValue: string;
	teamTwoValue: number;
	isPercentage?: boolean;
}

export interface IGroup {
	groupName: string;
	statisticItems: IStatisticItem[];
}

export interface IStatisticItem {
	away: string;
	awayValue: number;
	home: string;
	homeValue: number;
	key: string;
	name: string;
	statisticsType: StatisticsType;
	valueType: ValueType;
}

export enum StatisticsType {
	Negative = "negative",
	Positive = "positive",
}

export enum ValueType {
	Event = "event",
	Team = "team",
}

//#endregion

export enum StatusCodes {
	NOT_AVAILABLE = 4040020000,
	AVAILABLE = 2000000000,
}

export enum MatchStatAvailabilityIndex {
	AVAILABLE = 1,
	NOT_AVAILABLE = 0,
}

export enum MatchPeriods {
	ALL = "ALL",
	FIRST = "1ST",
	SECOND = "2ND",
	ET_FIRST = "ET1",
	ET_SECOND = "ET2",
}

//#region Pressure index types

export interface IHomeAwayTeams {
	homeTeam: string;
	awayTeam: string;
}

export interface PressureIndexConfig {
	periodDuration: number | null;
	periods: number | null;
	extraTime: boolean;
	overtimePeriodDuration: number | null;
	overtimePeriods: number | null;
	startingKeys: {
		minute: number;
	} | null;
	valueYMax?: number;
	valueKey: string;
	progressKey: string;
}

export interface PeriodPlaces {
	type: string;
	value: number;
}

//#endregion

//#region Match statistics types

export interface IPeriodStatisticsChartData {
	infoSection: string;
	key: string | null;
	home?: string;
	away?: string;
	title: string;
	data: TEventChartSeriesData;
	max: number;
}

export interface IGroupedPeriodStatisticsChartData {
	title: string;
	data: IPeriodStatisticsChartData[];
}

type TEventChartSeriesData = {
	home: string;
	away: string;
	homeValue: number;
	awayValue: number;
	statisticsType: StatisticsType;
};

//#endregion

//#region Player statistics types

export interface IPlayerStatGroup {
	title: string;
	key: string;
	data: IPlayerStat[];
}

export interface IPlayerStat {
	title: string;
	key: string;
	value?: string | number;
}

export type PlayersByTeamFilter = "all" | "home" | "away";

export type PlayerSortConfig = {
	key: string;
	direction: "asc" | "desc";
} | null;

//#endregion

// Streak stats region

export interface StreakStatsRequestSpecific {
	RequestType: RequestType;
	EventId: string;
}

export enum RequestType {
	Data = "data",
	Availability = "availability",
}

export interface StreakStatsRequestModel {
	specific: StreakStatsRequestSpecific;
}

export interface StreakApiResponse<T> {
	response: T;
	statusCode: number;
}

export interface StreakStatsAvailabilityResponse {
	data: StreakStatsAvailability;
	timeStamp: string;
}

export interface StreakStatsAvailability {
	headToHead: boolean;
	general: boolean;
	goalDistribution: {
		homeTeam: boolean;
		awayTeam: boolean;
	};
}

export interface StreakStatsResponse {
	data: StreakStatsData;
	timeStamp: string;
}

export interface StreakStatsData {
	headToHead: StreakStat[];
	goalDistribution: IGoalDistribution;
	general: StreakStat[];
}

export interface StreakStat {
	abrv: string;
	team: "home" | "away" | "both";
	value: string;
	margin?: number;
}

export interface MergedStreakStat extends StreakStat {
	homeValue: string | null;
	awayValue: string | null;
}

export interface IGoalDistribution {
	awayTeam: TeamGoalStats;
	homeTeam: TeamGoalStats;
}

export interface TeamGoalStats {
	concededGoals: number;
	matches: number;
	scoredGoals: number;
	periods: PeriodGoalStats[];
}

export interface PeriodGoalStats {
	concededGoals?: number;
	scoredGoals?: number;
	periodStart: number;
	periodEnd: number;
}

//#region  insights
export interface EventInsightsRequest {
	RequestType: RequestType;
	EventId: string;
	Language: string;
}

export interface EventInsightsData {
	data: {
		insights: string[];
	};
}
export interface EventInsightsAvailability {
	data: {
		insights: MatchStatAvailabilityIndex;
	};
}

//#endregion insights
